import { useState, useContext, useEffect, useMemo, useRef, createContext } from 'react'
import { TransitionGroup } from 'react-transition-group'

import _ from "lodash"
import ShortUniqueId from 'short-unique-id'
import { flatten } from "flat"

import { ToggleButtonGroup, ToggleButton, Autocomplete, Card, CardHeader, Slide, List, ListItem, Collapse, TextField, Typography, IconButton, Button, FormControlLabel, RadioGroup, Radio, Switch, Stack, Tooltip, CardContent, Paper } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import WestRoundedIcon from '@mui/icons-material/WestRounded'
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined'
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'

import { AppContext } from '/providers/app-context'
import { PassContext } from '../pass-context'
import dataSelector from './data-selector'
import currencies from '/utils/currencies.json'
import InstanceDataSelector from '/utils/instance-data-selector'
import getFormattedValue from './popup-editor-field-formatter'
import findPathByValue from '/utils/find-path-by-value'
import PopupShell from '../popup-shell'

const EditorContext = createContext({})

function shortUUID() {
  const uid = new ShortUniqueId({ length: 12 })
  const uuid = uid.rnd()

  const uuidArray = uuid.match(/.{1,4}/g)
  const formattedUUID = uuidArray.join("-")

  return formattedUUID
}

const PopupDialogFields = ({ open, close, title, path, maxFields, colors }) => {

  const { client } = useContext(AppContext)
  const { template, instanceData, setTemplate, scannerInstanceData, openInstanceDataManager } = useContext(PassContext)

  const [fields, setFields] = useState([])
  const [sourcePaths, setSourcePaths] = useState([])
  const [view, setView] = useState("list")

  const dataChanged = useMemo(() => {
    return !_.isEqual(fields, template[path])
  }, [fields])

  // Runs on "open" state change or if path changes
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    if (open) {
      let f = _.cloneDeep(template[path] ?? [])

      // Set some default values for any entries we do have that might not have all options set
      f.map(i => {
        if (!i.type) i.type = "text"
        if (!i.textAlignment) i.textAlignment = "PKTextAlignmentLeft"

        if (i.dateStyle) {
          if (i.isRelative === undefined) i.isRelative = false
        }

        if (i.dateStyle || i.timeStyle) {
          if (i.ignoresTimeZone === undefined) i.ignoresTimeZone = true
        }
      })

      setFields(f)
      setView("list")
    }

  }, [open, path, template])


  // Refresh our template / instance data source paths if instancedata changes
  useEffect(() => {
    setSourcePaths(Object.keys(flatten({ ...instanceData, __scanner_data__:{...scannerInstanceData} })).map(p => `{{${p}}}`).sort())
  }, [instanceData, scannerInstanceData])



  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const addItem = () => {

    const newFieldId = shortUUID()

    const newField = {
      key: newFieldId,
      label: "",
      value: "",
      type: "text",
      textAlignment: "PKTextAlignmentLeft"
    }

    setFields((prev) => [...prev, newField])
  }

  const resolveValue = (item, field) => {
    return dataSelector({ template: item, instanceData, scannerInstanceData, path: field })
  }

  const saveFormData = () => {
    let nt = _.cloneDeep(template)
    nt[path] = fields

    // Save content of our "new" template/instance data back to state
    if (!_.isEqual(nt, template)) setTemplate(nt)

    close()
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const limitedEditing = (item, field) => (field === "value" && client.client_id !== 0 && item.key.match(/__SMARTIX/) !== null)

  try {
    return (
      <PopupShell open={open} onClose={handleClose}>

        <div className="flex flex-col p-2 h-full w-full overflow-x-hidden">

          <div className="flex flex-row justify-between mt-5 w-full">
            <IconButton color='primary' onClick={addItem} disabled={fields?.length >= maxFields || view !== "list"}><AddCircleOutlineOutlinedIcon /></IconButton>
            <Typography color="secondary" variant="h6">{title}</Typography>
            <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
          </div>

          {colors?.label &&
            <div className="flex flex-col w-full mt-3">
              <div><b>label colour :</b> {colors.label}</div>
              <div><b>value colour :</b> {colors.value}</div>
            </div>
          }

          <hr/>

          <EditorContext.Provider value={{ resolveValue, limitedEditing, fields, setFields, sourcePaths, instanceData, scannerInstanceData, dataSelector, path }}>
            <CardsContainer view={view} setView={setView} path={path} />
          </EditorContext.Provider>

          <div className='fixed bottom-5 w-[460px]'>

            <hr className='mb-5' />

            <div className="flex flex-row w-full justify-between">
              <Button startIcon={<DatasetOutlinedIcon />} onClick={openInstanceDataManager} >Edit sample data</Button>
              <Button variant="contained" onClick={saveFormData} disabled={!dataChanged}>Save</Button>
            </div>
          </div>
        </div>
      </PopupShell>
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogFields


//////////////////////////////////////////////////////////////////////////////////////////////////////////
// Controlled JSX (Must have values passes as paramets and not access them from the parent 
// - hence outside the main function). Otherwise they do not update when data changes!
//////////////////////////////////////////////////////////////////////////////////////////////////////////



const CardsContainer = ({ view, setView }) => {

  const { fields } = useContext(EditorContext)
  const [selectedItem, setSelectedItem] = useState()

  useEffect(() => {
    const item = fields.filter(f => f.key === selectedItem?.key)[0]
    if (item) setSelectedItem(item)
  }, [fields])

  return (
    <div className='mt-5 mb-[100px] relative w-full h-full overflow-y-scroll overflow-x-hidden'>
      <CardList view={view} setView={setView} setSelectedItem={setSelectedItem} />
      <EditCard selectedItem={selectedItem} view={view} setView={setView} />
    </div>
  )
}

const CardList = ({ view, setView, setSelectedItem }) => {

  const endRef = useRef(null)
  const { fields } = useContext(EditorContext)

  // track previous length
  const prevLengthRef = useRef(fields.length)

  useEffect(() => {
    // update refs if items removed
    if (fields.length < prevLengthRef.current) {
      prevLengthRef.current = fields.length
    }
  }, [fields])


  return (

    <Slide
      direction="right"
      in={view === "list"}
      timeout={400}
      mountOnEnter
      unmountOnExit
    >
      <div className='absolute top-0 left-0 w-full'>
        <List disablePadding dense >
          <TransitionGroup>
            {fields.map((item, idx) => (
              <Collapse key={item.key}
                onEntered={() => {
                  // only scroll if a new item was added
                  if (
                    idx === fields.length - 1 &&
                    fields.length > prevLengthRef.current
                  ) {
                    endRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "end",
                    })
                  }
                  prevLengthRef.current = fields.length
                }}>
                <ItemCard
                  idx={idx}
                  item={item}
                  setView={setView}
                  value={item.value}
                  setSelectedItem={setSelectedItem}
                />
              </Collapse>
            ))}
          </TransitionGroup>
        </List>
        <div ref={endRef} />
      </div>
    </Slide>
  )
}


const ItemCard = ({ item, idx, setView, setSelectedItem }) => {

  const { resolveValue, fields, setFields } = useContext(EditorContext)

  const resolvedValue = resolveValue(item, "value") ?? ""
  const resolvedLabel = resolveValue(item, "label") ?? ""

  let title = resolvedLabel !== "" ? resolvedLabel : "<label not set>"
  let subtitle = getFormattedValue({ item, resolvedValue: resolvedValue !== "" ? resolvedValue : "<value not set>" })

  const edit = () => {
    setSelectedItem(item)
    setView("edit")
  }

  const deleteItem = item => {
    setFields((prev) => [...prev.filter((i) => i !== item)])
  }

  const moveItem = (item, direction) => {

    let fromIndex

    setFields((prev) => {
      fromIndex = _.indexOf(prev, item)
      return prev.filter((i) => i !== item)
    })

    setTimeout(() =>
      setFields((prev) => {
        const toIndex = fromIndex + (direction === "up" ? -1 : 1)
        return [
          ...prev.slice(0, toIndex),
          item,
          ...prev.slice(toIndex),
        ]
      }),
      300)
  }

  return (
    <ListItem disablePadding={true}>
      <Card variant="outlined" sx={{
        minHeight: "min-content",
        width: "448px",
        marginLeft : "-10px",
        position: "relative",
        overflow: "hidden",
        marginBottom: 1,
        bgcolor: 'rgba(0,0,0,0.1)'
      }}>
        <FieldPositionGraphic idx={idx} />
        <CardHeader
          title={title}
          subheader={subtitle}
          slotProps={{
            title: {
              sx: { color: resolvedValue.length ? "lightgray" : "darkgray", fontSize: "14px" }
            },
            subheader: {
              sx: { color: resolvedValue.length ? "secondary.main" : "darkgray", fontSize: "20px" }
            }
          }}
          style={{ cursor: "pointer" }}
          onClick={edit}
        />

        <div className='flex flex-row justify-between'>
          <div><IconButton sx={{ color: "red" }} onClick={() => deleteItem(item)}><DeleteForeverOutlinedIcon /></IconButton></div>
          <div>
            <IconButton disabled={idx === 0} onClick={() => moveItem(item, "up")}><ArrowCircleUpOutlinedIcon /></IconButton>
            <IconButton disabled={idx === (fields.length - 1)} onClick={() => moveItem(item, "down")}><ArrowCircleDownOutlinedIcon /></IconButton>
          </div>
          <div><IconButton color="primary" onClick={edit}><SettingsOutlinedIcon /></IconButton></div>
        </div>
      </Card>
    </ListItem>
  )
}

const FieldPositionGraphic = ({ idx }) => {

  const { fields, path } = useContext(EditorContext)
  if (path !== "backFields") {
    const items = fields.map((d, item) => <div key={item} className={"item" + (item === idx ? " selected" : "")}></div>)
    return <div className='field-position-graphic'>{items}</div>
  }
}


const EditCard = ({ selectedItem, view, setView }) => {

  const back = () => setView("list")

  if (selectedItem) return (
    <Slide
      direction="left"
      in={view === "edit"}
      timeout={400}
      mountOnEnter
      unmountOnExit
    >
      <div className='absolute top-0 left-0 w-full'>
        <Paper sx={{ bgcolor: 'background.paper', p: 1, position: "sticky", top: 0, left: 0, width: "100%", zIndex: 50 }} elevation={16}>
          <div className='flex flex-row justify-between'>
            <Button startIcon={<WestRoundedIcon />} onClick={back} >back</Button>
          </div>
        </Paper>

        <div className='overflow-y-scroll'>
          <List>
            <EditCardValueAndLabel item={selectedItem} field="label" />
            <EditCardValueAndLabel item={selectedItem} field="value" />
            <TextAlignment item={selectedItem} />
            <FieldFormatting item={selectedItem} />
            <FieldFormattingSubTypeWrapper item={selectedItem} />
            <DateTimeOptions item={selectedItem} />
            <ChangeMessage item={selectedItem} />
          </List>
        </div>
      </div>
    </Slide>
  )
}


const EditCardValueAndLabel = ({ item, field }) => {

  const { setFields, instanceData, scannerInstanceData, dataSelector, limitedEditing } = useContext(EditorContext)
  const limitedEditingOnly = limitedEditing(item, field)

  const initialMode = item[field]?.toString().match(/{{.*}}/) ? "instance" : "template"

  const [mode, setMode] = useState(initialMode)

  const handleChangeDataSource = value => {
    setMode(value)
    const needsExpertMode = (value === "instance") && item[field].length !== 0 && !/^{{[^}]*}}$/.test(item[field])

    if (!needsExpertMode) {
      const newValue = (value === "template") ? dataSelector({ template: item, path: field, instanceData, scannerInstanceData }) : findPathByValue(instanceData, item[field])
      const newItem = { ...item, [field]: newValue }

      setFields(prev => {
        let x = prev.map(i => i.key === newItem.key ? newItem : i)
        return x
      })
    }
  }

  return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <Typography sx={{ color: "secondary.main", marginLeft: 2, marginTop: 2 }}>{field}</Typography>

      <CardContent>
        <RadioGroup
          row
          aria-labelledby="data_type_label"
          value={mode}
          onChange={e => handleChangeDataSource(e.target.value)}
          className='mb-5'
        >
          <FormControlLabel value="template" control={<Radio disabled={limitedEditingOnly} />} label="same value for all passes" />
          <FormControlLabel value="instance" control={<Radio disabled={limitedEditingOnly} />} label="unique values for each pass" />
        </RadioGroup>

        {mode === "template" && <DataSourceTemplate item={item} field={field} limitedEditingOnly={limitedEditingOnly} />}
        {mode === "instance" && <DataSourceInstance item={item} field={field} limitedEditingOnly={limitedEditingOnly} />}
      </CardContent>

    </Card>
  )
}

const DataSourceTemplate = ({ item, field, limitedEditingOnly }) => {

  const { setFields, path } = useContext(EditorContext)

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f[field] = value
      return f
    }))
  }

  return (
    <TextField label={field}
      value={item[field]}
      slotProps={{ inputLabel: { shrink: true } }}
      type="search"
      disabled={limitedEditingOnly}
      onChange={e => { updateValue(e.target.value) }}
      fullWidth
      multiline={path === "backFields"}
      maxRows={5}
    />
  )
}


const DataSourceInstance = ({ item, field, limitedEditingOnly }) => {

  const startInExpertMode = item[field].length !== 0 && !/^{{[^}]*}}$/.test(item[field])
  const [expertMode, setExpertMode] = useState(startInExpertMode)

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }} className='mb-5'>
        <Typography>Simple selection</Typography>
        <Switch checked={expertMode} onChange={e => setExpertMode(e.target.checked)} disabled={limitedEditingOnly} />
        <Typography>Expert mode</Typography>
      </Stack>

      <SimpleInstanceSelector active={!expertMode} item={item} field={field} limitedEditingOnly={limitedEditingOnly} />
      <ExpertModeSelector active={expertMode} item={item} field={field} limitedEditingOnly={limitedEditingOnly} />
    </>
  )
}

const SimpleInstanceSelector = ({ active, item, field, limitedEditingOnly }) => {

  const { setFields, sourcePaths, resolveValue, instanceData, scannerInstanceData, dataSelector, path } = useContext(EditorContext)

  const resolvedValue = resolveValue(item, field)

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f[field] = value
      return f
    }))
  }

  const formattedValue = useMemo(() => getFormattedValue({ item, resolvedValue }), [resolvedValue, item.dateStyle, item.timeStyle, item.numberStyle, item.currencyCode])

  if (active) return (
    <>
      <InstanceDataSelector updateValue={updateValue} sourcePaths={sourcePaths} value={item[field]} dataSelector={dataSelector} instanceData={instanceData} scannerInstanceData={scannerInstanceData} limitedEditingOnly={limitedEditingOnly} />

      <TextField label="sample value (from instance data)"
        value={resolvedValue}
        slotProps={{ inputLabel: { shrink: true } }}
        type="search"
        fullWidth
        sx={{ mb: 1 }}
        disabled={true}
        multiline={path === "backFields"}
        maxRows={5}
      />

      {["number", "money", "date", "time", "datetime"].includes(item.type) &&
        <TextField label="formatted value"
          value={formattedValue}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
          disabled={true}
          multiline={path === "backFields"}
          maxRows={5}
        />
      }
    </>
  )
}


const ExpertModeSelector = ({ active, item, field, limitedEditingOnly }) => {

  const { setFields, sourcePaths, resolveValue, instanceData, scannerInstanceData, dataSelector, path } = useContext(EditorContext)
  const inputRef = useRef(null)

  const resolvedValue = resolveValue(item, field)

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f[field] = value
      return f
    }))
  }

  const insertAtCursor = insertText => {
    const input = inputRef.current
    if (!input) return

    const start = input.selectionStart
    const end = input.selectionEnd

    // New value with inserted text
    const newValue = item[field].substring(0, start) + insertText + item[field].substring(end)

    updateValue(newValue)

    // Update cursor position after insertion
    requestAnimationFrame(() => {
      input.setSelectionRange(start + insertText.length, start + insertText.length)
      input.focus()
    })
  }

  const formattedValue = useMemo(() => getFormattedValue({ item, resolvedValue }), [resolvedValue, item.dateStyle, item.timeStyle])

  if (active) return (
    <>
      <InstanceDataSelector updateValue={insertAtCursor} sourcePaths={sourcePaths} value={"choose"} dataSelector={dataSelector} instanceData={instanceData} scannerInstanceData={scannerInstanceData} limitedEditingOnly={limitedEditingOnly} />

      <TextField label="sample value (from instance data)"
        inputRef={inputRef}
        value={item[field]}
        slotProps={{ inputLabel: { shrink: true } }}
        type="search"
        onChange={e => { updateValue(e.target.value) }}
        sx={{ mb: 1 }}
        fullWidth
        multiline={path === "backFields"}
        maxRows={5}
      />

      <TextField label="resolved value"
        value={resolvedValue}
        slotProps={{ inputLabel: { shrink: true } }}
        type="search"
        disabled={true}
        fullWidth
        sx={{ mb: 1 }}
        multiline={path === "backFields"}
        maxRows={5}
      />

      {["number", "money", "date", "time", "datetime"].includes(item.type) &&
        <TextField label="formatted value"
          value={formattedValue}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
          disabled={true}
          multiline={path === "backFields"}
          maxRows={5}
        />
      }
    </>
  )
}


const TextAlignment = ({ item }) => {

  const { setFields, path } = useContext(EditorContext)

  if (["primaryFields", "backFields",].includes(path)) return

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f.textAlignment = value
      return f
    }))
  }

  return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <CardContent>
        <div className='flex flex-row justify-between w-full'>

          <Typography sx={{ color: "secondary.main" }}>Alignment</Typography>

          <ToggleButtonGroup
            value={item.textAlignment}
            exclusive
            onChange={(e, newValue) => updateValue(newValue)}
            aria-labelledby="text_align_label"
          >
            <ToggleButton value="PKTextAlignmentLeft" aria-label="left aligned">
              <FormatAlignLeftIcon />
            </ToggleButton>
            <ToggleButton value="PKTextAlignmentCenter" aria-label="centered">
              <FormatAlignCenterIcon />
            </ToggleButton>
            <ToggleButton value="PKTextAlignmentRight" aria-label="right aligned">
              <FormatAlignRightIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </CardContent>
    </Card>
  )
}

const FieldFormatting = ({ item }) => {

  const { setFields, path } = useContext(EditorContext)

  const updateValue = value => {
    setFields(prev => prev.map(f => {

      if (f.key === item.key) {

        f.type = value
        delete f.ignoresTimeZone
        delete f.isRelative
        delete f.dateStyle
        delete f.timeStyle
        delete f.numberStyle
        delete f.currencyCode

        switch (value) {
          case "number":
            if (!f.numberStyle) f.numberStyle = "PKNumberStyleDecimal"
            break

          case "date":
            if (!f.dateStyle) f.dateStyle = "PKDateStyleShort"
            if (!f.isRelative) f.isRelative = false
            if (!f.ignoresTimeZone) f.ignoresTimeZone = true
            break

          case "time":
            if (!f.timeStyle) f.timeStyle = "PKDateStyleShort"
            if (!f.ignoresTimeZone) f.ignoresTimeZone = true
            break

          case "datetime":
            if (!f.dateStyle) f.dateStyle = "PKDateStyleShort"
            if (!f.timeStyle) f.timeStyle = "PKDateStyleShort"
            if (!f.isRelative) f.isRelative = false
            if (!f.ignoresTimeZone) f.ignoresTimeZone = true
            break

          case "money":
            if (!f.currencyCode) f.currencyCode = "GBP"
            break
        }
      }

      return f
    }))
  }

  return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      padding: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <Typography sx={{ color: "secondary.main", margin: 1 }}>Formatting</Typography>

      <RadioGroup
        row
        aria-labelledby="field_formatting_label"
        value={item.type}
        onChange={e => updateValue(e.target.value)}
      >
        <FormControlLabel value="text" control={<Radio />} label="None (Text)" />
        <FormControlLabel value="number" control={<Radio />} label="Number" />
        <FormControlLabel value="date" control={<Radio />} label="Date" />
        <FormControlLabel value="time" control={<Radio />} label="Time" />
        <FormControlLabel value="datetime" control={<Radio />} label="Date and Time" />
        <FormControlLabel value="money" control={<Radio />} label="Money" />

        {path === "backFields" &&
          <>
            <FormControlLabel value="www" control={<Radio />} label="Website" />
            <FormControlLabel value="email" control={<Radio />} label="Email" />
            <FormControlLabel value="phone" control={<Radio />} label="Phone" />
            <FormControlLabel value="location" control={<Radio />} label="Map Location" />
          </>
        }
      </RadioGroup>
    </Card>)
}


const FieldFormattingSubTypeWrapper = ({ item }) => {

  if (item.type !== "text") return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      padding: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <FieldFormattingSubType item={item} />
    </Card>
  )
}

const FieldFormattingSubType = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateValue = (field, value) => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f[field] = value
      return f
    }))
  }

  switch (item.type) {
    case "number": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Number format</Typography>
          <RadioGroup
            value={item.numberStyle}
            onChange={e => updateValue("numberStyle", e.target.value)}
          >
            <FormControlLabel value="PKNumberStyleDecimal" control={<Radio />} label="Decimal" />
            <FormControlLabel value="PKNumberStylePercent" control={<Radio />} label="Percentage" />
            <FormControlLabel value="PKNumberStyleScientific" control={<Radio />} label="Scientific" />
            <FormControlLabel value="PKNumberStyleSpellOut" control={<Radio />} label="Spell Out" />
          </RadioGroup>
        </>
      )
    }

    case "date": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Date format</Typography>
          <RadioGroup
            value={item.dateStyle}
            onChange={e => updateValue("dateStyle", e.target.value)}
          >
            <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide date)" />
            <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (DD/MM/YY)" />
            <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (DD MMM YYYY)" />
            <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (DD MMMM YYYY)" />
            <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (dddd DD MMMM YYYY)" />
          </RadioGroup>
        </>
      )
    }

    case "time": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Time format</Typography>
          <RadioGroup
            value={item.timeStyle}
            onChange={e => updateValue("timeStyle", e.target.value)}
          >
            <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide time)" />
            <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (HH:MM)" />
            <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (HH:MM:SS)" />
            <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (HH:MM:SS GMT)" />
            <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (HH:MM:SS Greenwich Mean Time)" />
          </RadioGroup>
        </>
      )
    }

    case "datetime": {
      return (
        <div className='flex flex-col gap-10 w-full'>
          <div>
            <Typography sx={{ color: "secondary.main", margin: 1 }}>Date format</Typography>
            <RadioGroup
              value={item.dateStyle}
              onChange={e => updateValue("dateStyle", e.target.value)}
            >
              <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide date)" />
              <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (DD/MM/YY)" />
              <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (DD MMM YYYY)" />
              <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (DD MMMM YYYY)" />
              <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (dddd DD MMMM YYYY)" />
            </RadioGroup>
          </div>

          <div>
            <Typography sx={{ color: "secondary.main", margin: 1 }}>Time format</Typography>
            <RadioGroup
              value={item.timeStyle}
              onChange={e => updateValue("timeStyle", e.target.value)}
            >
              <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide time)" />
              <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (HH:MM)" />
              <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (HH:MM:SS)" />
              <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (HH:MM:SS GMT)" />
              <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (HH:MM:SS Greenwich Mean Time)" />
            </RadioGroup>
          </div>
        </div>
      )
    }

    case "money": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Currency</Typography>
          <Autocomplete
            disableClearable
            options={currencies}
            onChange={(e, newValue) => updateValue("currencyCode", newValue.code)}
            value={currencies.find(c => c.code === item.currencyCode)}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            renderInput={(params) => <TextField {...params} />}
            fullWidth />
        </>
      )
    }
  }
}


const DateTimeOptions = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateValue = (field, value) => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f[field] = value
      return f
    }))
  }

  useEffect(() => {
    if (["datetime", "date", "time"].includes(item.type)) {
      if (item.type === "time") {
        if (!!!item.ignoresTimeZone) updateValue("ignoresTimeZone", false)
      } else { // date / datetime
        if (!!!item.isRelative) updateValue("isRelative", false)
      }
    }
  }, [item])

  if (["datetime", "date", "time"].includes(item.type)) {

    return (
      <Card variant="outlined" sx={{
        minHeight: "min-content",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        marginBottom: 1,
        padding: 1,
        bgcolor: 'rgba(0,0,0,0.1)'
      }}>
        <Typography sx={{ color: "secondary.main", margin: 1 }}>Date/time options</Typography>

        <RadioGroup
          row
          aria-labelledby="field_formatting_label"
          value={item.ignoresTimeZone}
          onChange={e => updateValue("ignoresTimeZone", e.target.value)}
        >
          <FormControlLabel value={true} control={<Radio />} label="Ignore time zone" />
          <FormControlLabel value={false} control={<Radio />} label="Adjust to user's time zone" />
        </RadioGroup>

        {["datetime", "date"].includes(item.type) &&
          <RadioGroup
            row
            aria-labelledby="field_formatting_label"
            value={item.isRelative}
            onChange={e => updateValue("isRelative", e.target.value)}
          >
            <FormControlLabel value={false} control={<Radio />} label={`Display as absolute date`} />
            <FormControlLabel value={true} control={<Radio />} label={`Display as relative date`} />
          </RadioGroup>
        }
      </Card>
    )
  }
}


const ChangeMessage = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f.changeMessage = value
      return f
    }))
  }

  const mode = item.value?.toString().match(/{{.*}}/) ? "instance" : "template"

  if (mode === "instance") return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",  
      width: "100%",
      position: "relative",
      overflow: "hidden",
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <CardContent>
        <Typography sx={{ color: "secondary.main", marginBottom: 1 }}>Change Message</Typography>
        <TextField
          value={item.changeMessage}
          type="search"
          onChange={e => { updateValue(e.target.value) }}
          fullWidth
        />
        <p className='mt-2'><b>NOTE:</b> Must contain <b>%@</b>. This will be substituted with the new value.</p>
        <p className='mt-2'>eg. "Date changed to %@" will appear as "Date changed to 12 May 2026" if the new value is "12 May 2026"</p>
        <p className='mt-2'>Apple will display a generic change message if you do not include <b>%@</b>.</p>

      </CardContent>
    </Card>
  )
}
